import { useContext, useEffect, useState } from 'react'
// import { useNavigate } from 'react-router-dom'
import {
  Box,
  BoxProps,
  Button,
  Flex,
  IconButton,
  Image,
  Stack,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'

// import { offerStatusColorSchemes } from '../../../data/offers/utils/offerStatusColorSchemes'
import { logos } from '../../../utils/companiesLogos'
import { Check, Duplicate, Pdf, Refresh, Settings } from '../../../icons'
import { formatPrice } from 'BrokerTsLib/src/Utils/formatPrice'
import { OfferPackageColumnSection } from './OfferPackageColumnSection'
import { useOfferGrid } from '../../../context/OfferGridContext'
import { offerStatusColors } from '../../../data/offers/utils/offerStatusColors'
import { LoadingOverlay, LoadingOverlayContext } from '../../../components/LoadingOverlay'
import { StatusBadge } from './StatusBadge'
import { offerStatusEnum } from 'BrokerTsLib/src/enums/offerStatusEnum'
import {
  Document,
  OfferPackage,
  OfferProperty,
  PackageItemModel,
  PackageItemPropertyModel,
  PartPackageProperty,
  Variant,
} from 'BrokerTsLib/src/dtos'
import { useNavigate } from 'react-router-dom'
import { useInsuranceParamsContext } from '../../../layouts/Wizard'
import { usePropertyOffersContext } from '../../../context/PropertyOffersProvider'
import {
  useClone,
  useGetOfferAvailableAddons,
  useGetOfferAvailableDiscounts,
  useSelectCalculation,
  useSetOfferIncluded,
} from '../../../hooks/useOffer'
import { useGetOfferAvailablePackageItems, useSaveOfferParts } from '../../../hooks/useOfferPackage'
import { useUpdateLastWizardStep } from '../../../hooks/useWizard'
import { useGetGroupedRisks } from '../../../hooks/useEnumProperty'
import { useGetOfferDocuments } from '../../../hooks/useDocuments'
import { MenuItem, MenuItemWithSubMenu, SubMenu } from '../../../components/Menu'
import moment from 'moment'
import { DiscountCell } from './components/DiscountCell'
import { IBodyCell } from './columnSections'
import { excludeRisksInGrid } from './OfferPropertyHeaderColumn'
import { OfferPackageSettingsForm } from './OfferPackageSettingsForm'
import { Risk } from 'BrokerTsLib/src/enums'
import { OfferCell } from './components/OfferCell'
import { OfferCellPayment } from './components/OfferCellPayment'
import { OfferCellWrapper } from './components/OfferCellWrapper'
// import { useOfferProperty } from '../../context/OfferPropertyContext'

const documentUrl = `${process.env.REACT_APP_DOCUMENT_PROPERTY_API}`

const OfferColumnSection = ({ children, sx, ...rest }: BoxProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 2,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  )
}

export interface OfferProps {
  offer: OfferProperty
  onToggle?: (included: boolean) => void
  variant: Variant
}

export const OfferPropertyColumn = ({ offer, onToggle, variant }: OfferProps) => {
  const navigate = useNavigate()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const insuranceGroups = useGetGroupedRisks(variant, excludeRisksInGrid)
  const responsibility = insuranceGroups['0']?.find((x) => x.id == Risk.RESPONSIBILITY)
  const responsibilityRealestate = insuranceGroups['0']?.find(
    (x) => x.id == Risk.RESPONSIBILITY_REALESTATE,
  )
  const spolProperty = insuranceGroups['0']?.find((x) => x.id == Risk.SPOL_PROPERTY)
  const spolFlood = insuranceGroups['0']?.find((x) => x.id == Risk.SPOL_FLOOD)

  const { insuranceParamsId, insuranceParams, refetchInsuranceParams } = useInsuranceParamsContext()
  const {
    updateOffer,
    calculatingOffersIds,
    recalculateOffers: recalculate,
  } = usePropertyOffersContext()
  const { data: dataOfferAvailableAddons } = useGetOfferAvailableAddons(offer!.offerId)
  const { data: dataOfferAvailablePackageItems } = useGetOfferAvailablePackageItems(offer!.offerId)
  const addons = (dataOfferAvailableAddons?.data ?? []) as PackageItemPropertyModel[]
  const packageItems = (dataOfferAvailablePackageItems?.data ?? []) as PackageItemPropertyModel[]

  const isRecalculating = calculatingOffersIds?.includes(offer!.offerId) ?? false
  const { setLoading } = useContext(LoadingOverlayContext)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { mutateAsync: selectOffer } = useSelectCalculation()
  const { mutateAsync: saveOfferParts } = useSaveOfferParts()
  const { mutateAsync: cloneOffer } = useClone(offer.offerId)
  const { mutateAsync: updateStep } = useUpdateLastWizardStep()
  const { openedColumns, onSectionTitleClick } = useOfferGrid()

  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const displayId = `${offer.offerId}/${insuranceParamsId}`
  const elementId = `offer-${displayId.replace(/\//, '')}`

  const hasError = (offer.errors?.length ?? 0) > 0
  const errMsg = hasError ? offer.errors?.map((e) => e.message).join('<br/>') : ''

  let status = offerStatusEnum.Valid
  if (hasError) status = offerStatusEnum.Error
  else if (insuranceParams?.generalParams.selectedOfferId === offer.offerId)
    status = offerStatusEnum.Selected

  const color = offerStatusColors[status]
  const logo = logos[offer.insurerId]

  const { mutateAsync: setIncluded } = useSetOfferIncluded()

  const handleToggle = (included: boolean) => {
    onToggle && onToggle(included)

    setTimeout(() => {
      offer!.included = included
      updateOffer(offer!)
      setIncluded({ offerId: offer?.offerId!, included: included })
    }, 300)
  }

  const saveCurrentState = () => {
    setLoading(true)
    saveOfferParts(offer)
      .then(() => recalculate([offer.offerId], true))
      .finally(() => setLoading(false))
  }

  const [offerDocuments, setOfferDocuments] = useState<Document[]>([])
  const { refetch: getOfferDocuments } = useGetOfferDocuments(offer!.offerId, {
    enabled: false,
    onSuccess: (data) => {
      if (data.data?.length > 0) setOfferDocuments([...data.data])
      else setOfferDocuments([])
    },
  })
  const { refetch: getOfferAvailableDiscounts, data: offerDiscounts } =
    useGetOfferAvailableDiscounts(offer!.offerId, { enabled: false })

  // doctu nove dokumenty po kazde rekalkulaci
  useEffect(() => {
    setLoading(isRecalculating)

    if (!isRecalculating)
      setTimeout(() => {
        getOfferDocuments() // at nebrani nacteni jinym dulezitejsim requestum
        getOfferAvailableDiscounts() // slevy se mohou po rekalkulaci zmenit
      }, 5000)
  }, [isRecalculating])

  const offerDoc = offerDocuments.find((x) => x.name === 'OfferDocument')
  const xmlDocs = offerDocuments.filter((x) => x.name !== 'OfferDocument')
  const changed = offer.parts.findIndex((x) => !!x.changed) >= 0

  const handleSettingsClick = () => {
    onOpen()
  }

  useEffect(() => {
    if (changed) saveCurrentState()
  }, [changed])

  if (!packageItems || packageItems.length == 0) return null

  let responsibilityOfferPart = offer.parts.find((op) => op.riskId == Risk.RESPONSIBILITY)
  const responsibilityPackageItem = packageItems.find((x) => x.riskId == Risk.RESPONSIBILITY)!

  let responsibilityRealestateOfferPart = offer.parts.find(
    (op) => op.riskId == Risk.RESPONSIBILITY_REALESTATE,
  )
  const responsibilityRealestatePackageItem = packageItems.find(
    (x) => x.riskId == Risk.RESPONSIBILITY_REALESTATE,
  )!

  let spolPropertyOfferPart = offer.parts.find((op) => op.riskId == Risk.SPOL_PROPERTY)
  const spolPropertyPackageItem = packageItems.find((x) => x.riskId == Risk.SPOL_PROPERTY)!

  if (!spolPropertyOfferPart) {
    spolPropertyOfferPart = {
      partId: 0,
      riskId: Risk.SPOL_PROPERTY,
      offerId: offer.offerId,
      packageItem: spolPropertyPackageItem,
    } as PartPackageProperty

    offer.parts.push(spolPropertyOfferPart)
  }

  let spolPropertyOfferParts = offer.parts.filter((op) => op.riskId == Risk.SPOL_PROPERTY)
  const spolPropertyPackageItems = packageItems.filter((x) => x.riskId == Risk.SPOL_PROPERTY)!

  let spolFloodOfferPart = offer.parts.find((op) => op.riskId == Risk.SPOL_FLOOD)
  const spolFloodPackageItem = packageItems.find((x) => x.riskId == Risk.SPOL_FLOOD)!

  if (!spolFloodOfferPart) {
    spolFloodOfferPart = {
      partId: 0,
      riskId: Risk.SPOL_FLOOD,
      offerId: offer.offerId,
      packageItem: spolFloodPackageItem,
    } as PartPackageProperty

    offer.parts.push(spolFloodOfferPart)
  }

  let spolFloodOfferParts = offer.parts.filter((op) => op.riskId == Risk.SPOL_FLOOD)
  const spolFloodPackageItems = packageItems.filter((x) => x.riskId == Risk.SPOL_FLOOD)!
  const floodRiskIncluded = offer.parts.some((op) => op.riskId == Risk.FLOOD && op.included)

  if (!responsibilityOfferPart) {
    responsibilityOfferPart = {
      partId: 0,
      riskId: Risk.RESPONSIBILITY,
      offerId: offer.offerId,
      packageItem: responsibilityPackageItem,
    } as PartPackageProperty

    offer.parts.push(responsibilityOfferPart)
  }

  if (!responsibilityRealestateOfferPart) {
    responsibilityRealestateOfferPart = {
      partId: 0,
      riskId: Risk.RESPONSIBILITY_REALESTATE,
      offerId: offer.offerId,
      packageItem: responsibilityRealestatePackageItem,
    } as PartPackageProperty

    offer.parts.push(responsibilityRealestateOfferPart)
  }

  return (
    <LoadingOverlay isLoading={isSubmitting}>
      <Box
        id={elementId}
        sx={{
          display: 'grid',
          gap: 'px',
          placeContent: 'stretch',
          borderLeft: '1px solid',
          borderLeftColor: 'gray.300',
          minW: 230,
        }}
      >
        {/* id, included... */}
        <OfferColumnSection
          sx={{
            px: 2,
            py: 1,
            bg: color,
          }}
        >
          <Flex sx={{ gap: 2 }}>
            <Text sx={{ fontSize: 'sm', fontWeight: 'medium' }}>{`#${displayId}`}</Text>
            {status && <StatusBadge status={status} tooltip={errMsg} />}
          </Flex>

          <Flex sx={{ alignItems: 'center', gap: 2 }}>
            <Switch
              id={`offer-toggle-${displayId}`}
              size="sm"
              isDisabled={isContracted}
              isChecked={offer?.included}
              onChange={(e) => handleToggle(e.target.checked)}
            />

            {/* <Flex>
              <IconButton bg="transparent" aria-label="Posunout doleva">
                <ChevronLeft width={3} height={3} />
              </IconButton>
              <IconButton bg="transparent" aria-label="Posunout doprava">
                <ChevronRight width={3} height={3} />
              </IconButton>
            </Flex> */}
          </Flex>
        </OfferColumnSection>

        {/* logo, sjednat button */}
        <OfferColumnSection
          sx={{
            gap: 8,
            px: 2,
            py: 0,
          }}
        >
          <Image
            src={logo}
            alt={offer.insurerName}
            width="64px"
            height="48px"
            style={{ mixBlendMode: 'multiply' }}
          />

          {status !== offerStatusEnum.Error && !isContracted && !changed && (
            <Button
              colorScheme="primary"
              size="sm"
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
              loadingText="Ukládám..."
              onClick={() => {
                setIsSubmitting(true)

                selectOffer({ paramsId: insuranceParamsId!, offerId: offer.offerId })
                  .then(() => {
                    if ((insuranceParams?.generalParams.lastWizardStep ?? 3) < 4)
                      updateStep({ paramsId: Number(insuranceParamsId!), step: 4 }).then(() =>
                        refetchInsuranceParams().then(() => {
                          navigate('/chaloupka/wizard/4/' + insuranceParamsId!)
                        }),
                      )
                    else
                      refetchInsuranceParams().then(() => {
                        navigate('/chaloupka/wizard/4/' + insuranceParamsId!)
                      })
                  })
                  .finally(() => setIsSubmitting(false))
              }}
            >
              <Check />
              Sjednat
            </Button>
          )}

          {/* {changed && (
            <Button colorScheme="blue" size="sm" onClick={saveCurrentState}>
              <Refresh />
              Přepočítat
            </Button>
          )} */}

          {status === offerStatusEnum.Error && (
            <Tooltip
              label={
                <Stack direction={'column'}>
                  {offer?.errors.map((e, i) => {
                    return (
                      <Text key={i} variant="calcError" whiteSpace={'break-spaces'}>
                        {e.message}
                      </Text>
                    )
                  })}
                </Stack>
              }
              hasArrow
              placement="top"
            >
              <Text sx={{ color: 'red.800', fontSize: 'xs', fontWeight: 'semibold' }}>Chyba</Text>
            </Tooltip>
          )}
        </OfferColumnSection>

        {/* price */}
        <OfferColumnSection
          sx={{
            p: 2,
            bg: color,
          }}
        >
          <Text sx={{ fontSize: 'sm', fontWeight: 'medium' }}>{offer.title}</Text>

          <Text sx={{ fontSize: 'sm', fontWeight: 'semibold' }}>
            {changed ? '???' : formatPrice({ price: offer.price })}
          </Text>
        </OfferColumnSection>

        {/* buttons */}
        <OfferColumnSection
          sx={{
            gap: 8,
            px: 2,
          }}
        >
          <MenuItem variant="ghost" colorScheme="gray" color="gray.500" size="sm">
            &nbsp;
          </MenuItem>

          {/* <Stars /> */}

          <Flex sx={{ gap: 1, flex: 1, justifyContent: 'end' }}>
            {xmlDocs && xmlDocs.length > 0 && (
              <MenuItemWithSubMenu
                trigger="click"
                button={
                  <MenuItem variant="ghost" colorScheme="gray" color="gray.500" size="sm">
                    XML
                  </MenuItem>
                }
                subMenu={
                  <SubMenu
                    sx={{
                      maxHeight: '400px',
                      overflow: 'auto',
                      p: 2,
                      width: 'max-content',
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      bg: 'white',
                      borderRadius: 'md',
                      boxShadow: 'md',
                      listStyle: 'none',
                      zIndex: 'subMenu',
                    }}
                  >
                    <li>
                      {xmlDocs.map((doc) => {
                        return (
                          <MenuItem
                            key={doc.id}
                            target="_blank"
                            href={`${documentUrl}/GetOfferDocument?docId=${doc.id}`}
                          >
                            {doc.name} ({moment(doc.date).format('D.M.yyyy H:mm:ss')})
                          </MenuItem>
                        )
                      })}
                    </li>
                  </SubMenu>
                }
              />
            )}

            {offerDoc && (
              <Tooltip label="Stáhnout PDF nabídku pojišťovny" hasArrow placement="top">
                <IconButton
                  variant="ghost"
                  aria-label="PDF"
                  as={'a'}
                  target="_blank"
                  href={`${documentUrl}/GetOfferDocument?docId=${offerDoc.id}`}
                >
                  <Pdf color="secondary.500" />
                </IconButton>
              </Tooltip>
            )}

            {!isContracted && (
              <>
                <Tooltip label="Přepočítat nabídku" hasArrow placement="top">
                  <IconButton
                    variant="ghost"
                    aria-label="Obnovit"
                    onClick={() => {
                      recalculate([offer.offerId], true).finally(() => {
                        getOfferDocuments()
                      })
                    }}
                  >
                    <Refresh color="secondary.500" />
                  </IconButton>
                </Tooltip>

                <Tooltip label="Klonovat nabídku" hasArrow placement="top">
                  <IconButton
                    variant="ghost"
                    aria-label="Klonovat"
                    onClick={() => {
                      cloneOffer().then(({ data: offer }) => {
                        updateOffer(offer as OfferProperty)
                        recalculate([offer.offerId], true)
                      })
                    }}
                  >
                    <Duplicate color="secondary.500" />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {addons.length > 0 && (
              <Tooltip label="Další doplňková pojištění" hasArrow placement="top">
                <IconButton variant="ghost" aria-label="Nastavení" onClick={handleSettingsClick}>
                  <Settings color="secondary.500" />
                </IconButton>
              </Tooltip>
            )}
          </Flex>
        </OfferColumnSection>

        {/* risks in groups */}
        {insuranceGroups &&
          Object.keys(insuranceGroups).map((key) => {
            const items = insuranceGroups[key].filter(
              (x) =>
                ![
                  Risk.RESPONSIBILITY,
                  Risk.RESPONSIBILITY_REALESTATE,
                  Risk.SPOL_PROPERTY,
                  Risk.SPOL_FLOOD,
                ].includes(x.id),
            )
            const section = items[0]

            let titleHouseholdAndRealty: React.ReactNode = null
            if (section.groupId == '0') {
              const ids = items.map((x) => x.id)
              let isHouseholdAndRealty = false
              ids.forEach((riskId) => {
                if (offer.parts.filter((op) => op.packageItem.riskId == riskId).length > 1) {
                  isHouseholdAndRealty = true
                }
              })
              if (isHouseholdAndRealty) {
                titleHouseholdAndRealty = (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(2, 1fr)`,
                      justifyContent: 'end',
                      width: '100%',
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>Domácnost</Box>
                    <Box sx={{ textAlign: 'center' }}>Stavba</Box>
                  </Box>
                )
              }
            }

            return (
              <OfferPackageColumnSection
                onClick={() => {
                  onSectionTitleClick(section.groupId)
                }}
                isOpened={openedColumns.includes(section.groupId)}
                key={section.groupId}
                titleBg={color}
                titleWhenOpened={titleHouseholdAndRealty}
                bodyCells={items.map((x) => {
                  const bodyCells = packageItems
                    .filter((pi) => pi.riskId == x.id)
                    .sort(
                      (x, y) =>
                        (x.productTypeRiskVariantId ?? 0) - (y.productTypeRiskVariantId ?? 0),
                    )
                    .map((pi) => {
                      let op = offer.parts.find(
                        (op) => op.packageItem.packageItemId == pi.packageItemId,
                      )
                      if (!op) {
                        op = {
                          partId: 0,
                          riskId: x.id,
                          offerId: offer.offerId,
                          packageItem: pi,
                        } as PartPackageProperty

                        offer.parts.push(op)
                      }

                      return {
                        id: x.id.toString(),
                        offerPart: op,
                        packageItem: pi,
                      } as IBodyCell
                    })

                  return bodyCells
                })}
              />
            )
          })}

        {responsibility && (
          <OfferCell
            id={Risk.RESPONSIBILITY.toString()}
            offerPart={responsibilityOfferPart}
            packageItem={responsibilityPackageItem}
          />
        )}

        {responsibilityRealestate && (
          <OfferCell
            id={Risk.RESPONSIBILITY_REALESTATE.toString()}
            offerPart={responsibilityRealestateOfferPart}
            packageItem={responsibilityRealestatePackageItem}
          />
        )}

        {spolProperty && spolPropertyOfferParts.length > 1 && (
          <Box
            key={'spolBox'}
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${spolPropertyOfferParts.length}, 1fr)`,
              justifyContent: 'end',
            }}
          >
            {spolPropertyOfferParts
              .sort(
                (a, b) =>
                  a.packageItem.productTypeRiskVariantId! - b.packageItem.productTypeRiskVariantId!,
              )
              .map((spolOfferPart, i) => (
                <OfferCell
                  key={i}
                  id={Risk.SPOL_PROPERTY.toString()}
                  offerPart={spolOfferPart}
                  packageItem={
                    spolPropertyPackageItems.find(
                      (pi) => pi.packageItemId == spolOfferPart.packageItem.packageItemId,
                    )!
                  }
                  singleCell={false}
                />
              ))}
          </Box>
        )}

        {spolProperty && spolPropertyOfferParts.length == 1 && (
          <OfferCell
            id={Risk.SPOL_PROPERTY.toString()}
            offerPart={spolPropertyOfferPart}
            packageItem={spolPropertyPackageItem}
          />
        )}

        {spolFlood && floodRiskIncluded && spolFloodOfferParts.length > 1 && (
          <Box
            key={'spolFoodBox'}
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${spolFloodOfferParts.length}, 1fr)`,
              justifyContent: 'end',
            }}
          >
            {spolFloodOfferParts
              .sort(
                (a, b) =>
                  a.packageItem.productTypeRiskVariantId! - b.packageItem.productTypeRiskVariantId!,
              )
              .map((spolOfferPart, i) => (
                <OfferCell
                  key={i}
                  id={Risk.SPOL_FLOOD.toString()}
                  offerPart={spolOfferPart}
                  packageItem={
                    spolFloodPackageItems.find(
                      (pi) => pi.packageItemId == spolOfferPart.packageItem.packageItemId,
                    )!
                  }
                  singleCell={false}
                />
              ))}
          </Box>
        )}

        {spolFlood && floodRiskIncluded && spolFloodOfferParts.length == 1 && (
          <OfferCell
            id={Risk.SPOL_FLOOD.toString()}
            offerPart={spolFloodOfferPart}
            packageItem={spolFloodPackageItem}
          />
        )}

        {spolFlood &&
          !floodRiskIncluded /* pokud nabídka neobsahuje povodeň, zobrazujeme pouze prázdnou buňku */ && (
            <OfferCellWrapper
              key={Risk.SPOL_FLOOD.toString()}
              sx={{ justifyContent: 'end', justifyItems: 'end' }}
            >
              <Flex>
                <Text>&nbsp;</Text>
              </Flex>
            </OfferCellWrapper>
          )}

        <DiscountCell offer={offer} isDisabled={isContracted} />

        <OfferCellPayment offerId={offer.offerId} isDisabled={isContracted} />
      </Box>

      {
        <OfferPackageSettingsForm
          isOpen={isOpen}
          onClose={onClose}
          onSuccess={() => recalculate([offer.offerId])}
          offer={offer}
          title={`Doplňkové pojištění pro nabídku #${displayId}`}
          isReadOnly={isContracted}
        />
      }
    </LoadingOverlay>
  )
}
